import { ChatError, type ChatReply, type ChatTurn, type Source } from './api'

/**
 * THE CONVERSATION, HELD OUTSIDE THE PAGE (Kelvo pre-pilot, batch 2v).
 *
 * WHY. The staff routes swap whole pages. Going from the chat to Questions,
 * Documents or Facts unmounted the chat, and the conversation lived in the
 * chat's own React state, so it died with it: back on the chat, an empty
 * screen. Seen in every demo. Worse, a reply still on its way when the owner
 * left was written into a page that no longer existed, and was lost.
 *
 * WHAT CHANGED. The conversation (messages, sending, error, the unsent draft)
 * lives in a small store. The page READS it and draws it; the store SENDS the
 * turn and writes the reply into itself. Leave the page and come back, and the
 * conversation is there, including a reply that arrived while you were away.
 *
 * MEMORY ONLY, ON PURPOSE. Not localStorage, not sessionStorage. A site tablet
 * is shared, and a conversation can hold what one person's drawers allow and
 * another's do not. Nothing written to the browser's disk means nothing left
 * behind on it. A reload starts a fresh conversation, which is what a reload
 * should do.
 *
 * ONE PERSON, ONE CONVERSATION. The staff store is keyed to the signed-in
 * Clerk user. If a different person signs in (one signs out, another signs in
 * on the same tablet, without a reload), the old conversation is discarded,
 * not handed over, and a reply still in flight for the old person is dropped
 * when it lands. Signing out discards it too. See staffConversation().
 *
 * THE PUBLIC CHAT IS UNCHANGED. Foredesk and every public pod mount the chat
 * with no store, and App makes its own local one that dies with the page:
 * exactly the behaviour they had before this file existed.
 */

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
}

export interface ConversationState {
  messages: Message[]
  status: 'idle' | 'sending'
  error: string | null
  /** The unsent draft in the composer, so a half-typed question survives too. */
  input: string
}

export type Transport = (history: ChatTurn[]) => Promise<ChatReply>

export interface ConversationStore {
  /** For useSyncExternalStore: the same object until something changes. */
  getState(): ConversationState
  subscribe(listener: () => void): () => void
  setInput(input: string): void
  /** Send a question. False if it was empty or a turn is already in flight. */
  send(text: string, transport: Transport): boolean
  /** Ask the engine again with the conversation as it stands. */
  retry(transport: Transport): boolean
  /**
   * Retire this store for good: its state is emptied, its listeners dropped,
   * and every later write (a reply landing late) is ignored.
   */
  discard(): void
  /** True once discarded. */
  readonly discarded: boolean
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

function uid(): string {
  return crypto.randomUUID()
}

export function createConversationStore(): ConversationStore {
  let state: ConversationState = { messages: [], status: 'idle', error: null, input: '' }
  let live = true
  const listeners = new Set<() => void>()

  function set(patch: Partial<ConversationState>): void {
    if (!live) return
    state = { ...state, ...patch }
    for (const listener of [...listeners]) listener()
  }

  async function runEngine(history: Message[], transport: Transport): Promise<void> {
    set({ status: 'sending', error: null })
    try {
      const reply = await transport(history.map((m) => ({ role: m.role, content: m.content })))
      set({
        messages: [
          ...state.messages,
          { id: uid(), role: 'assistant', content: reply.answer, sources: reply.sources },
        ],
      })
    } catch (e) {
      set({ error: e instanceof ChatError ? e.message : GENERIC_ERROR })
    } finally {
      set({ status: 'idle' })
    }
  }

  return {
    getState: () => state,
    subscribe(listener) {
      if (!live) return () => {}
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    setInput(input) {
      set({ input })
    },
    send(text, transport) {
      const content = text.trim()
      if (!live || !content || state.status === 'sending') return false
      const next: Message[] = [...state.messages, { id: uid(), role: 'user', content }]
      set({ messages: next, input: '' })
      void runEngine(next, transport)
      return true
    },
    retry(transport) {
      if (!live || state.status === 'sending' || state.messages.length === 0) return false
      void runEngine(state.messages, transport)
      return true
    },
    discard() {
      if (!live) return
      state = { messages: [], status: 'idle', error: null, input: '' }
      live = false
      listeners.clear()
    },
    get discarded() {
      return !live
    },
  }
}

/** The staff chat's one conversation, and whose it is. Module memory only. */
let staff: { userId: string; store: ConversationStore } | null = null

/**
 * The staff conversation for this signed-in person. The same store every time
 * for the same person, so leaving the chat and coming back finds it. A
 * different person gets a new, empty store, and the previous one is discarded
 * before anything can draw it.
 */
export function staffConversation(userId: string): ConversationStore {
  if (staff && staff.userId === userId) return staff.store
  staff?.store.discard()
  staff = { userId, store: createConversationStore() }
  return staff.store
}

/** Signed out: nobody's conversation stays in memory. */
export function forgetStaffConversation(): void {
  staff?.store.discard()
  staff = null
}
